import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '@/api/apiCall';
import { toast } from 'sonner';
import jeddhalogo from '@/assets/img/jeddha-logo.png'
import { AssetsPanel } from '../shared/AssetsPanel';
import LoadingComponent from '@/components/common/LoadingComponent';
import { API_CALL_TIME } from '@/constants/constant';

const AirportEnforcementProfileTable = ({ enforcementData, year, monthNumber, contract }) => {
  const [data, setData] = useState(null);
  const dataRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);

  // ─── Refs for table inputs (uncontrolled) ───────────────────────────────────
  const violationRefs = useRef([]);  // violationRefs.current[rowIndex][colIndex]
  const assetSubCountRefs = useRef([]);
  const assetTotalCountRefs = useRef([]);
  
  // Section input ref
  const sectionRef = useRef(null);
  // ────────────────────────────────────────────────────────────────────────────

  const getDefaultStructure = () => ({
    profileName: 'Jeddah Airport Enforcement Profile',
    section: 'Assets Deployed',
    violations: {
      columns: ["#", "english", "code", "escalation", "amount", "arabic", "arabic_no"],
      rows: Array(17).fill(null).map((_, i) => [i + 1, '', null, '', 0, '', i + 1]),
      notes: [
        ["Tow Charge", 200],
        ["Clamp Charge", 200],
        ["Storage: 120/day (start when the violated vehicle enters the Pound)", 120],
        ["Vehicle Towing Service", 200]
      ]
    },
    assetsDeployed: [
      { name: 'Ticket Dispenser', isCategory: true, subCount: '', totalCount: '' },
      { name: 'DIP G100', isCategory: false, subCount: '', totalCount: '' },
      { name: 'G100 64 Bit', isCategory: false, subCount: '', totalCount: '' },
      { name: 'G100 32 Bit', isCategory: false, subCount: '', totalCount: '' },
    ],
    apiParams: { contract, year, month: monthNumber }
  });

  useEffect(() => {
    if (enforcementData && Object.keys(enforcementData).length > 0) {
      setData(enforcementData);
      dataRef.current = enforcementData;
    } else {
      const defaultData = getDefaultStructure();
      setData(defaultData);
      dataRef.current = defaultData;
    }
  }, [enforcementData]);

  const {
    profileName = 'Jeddah Airport Enforcement Profile',
    section = 'Assets Deployed',
    violations = {},
    assetsDeployed = [],
    apiParams = {}
  } = data || {};

  const autoSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveToAPI();
    }, API_CALL_TIME);
  };

  // ─── Read refs → build API payload ─────────────────────────────────────────
  const transformToApiFormat = () => {
    const currentSection = sectionRef.current?.value ?? section;
    
    return {
      contract: apiParams.contract || "0020018",
      year: parseInt(apiParams.year) || 2025,
      month: parseInt(apiParams.month) || 10,
      created_by: 1,
      tables: [
        {
          table_key: "jeddah_airport_enforcement_profile_summary",
          title: "Jeddah Airport Enforcement Profile",
          data: {
            profile_name: profileName,
            section: currentSection
          }
        },
        {
          table_key: "violations_table_fees",
          title: "Violations Table & Fees",
          data: {
            columns: violations.columns || [],
            rows: (violations.rows || []).map((row, rowIndex) =>
              row.map((cell, colIndex) =>
                violationRefs.current[rowIndex]?.[colIndex]?.value ?? cell
              )
            ),
            notes: { items: violations.notes || [] }
          }
        },
        {
          table_key: "assets_deployed_enforcement",
          title: "Jeddah Airport Enforcement - Assets Deployed",
          data: {
            groups: transformAssetsToApiFormat()
          }
        }
      ]
    };
  };

  const transformAssetsToApiFormat = () => {
    const currentAssets = dataRef.current?.assetsDeployed || assetsDeployed;
    const groups = [];
    let currentGroup = null;

    currentAssets.forEach((asset, i) => {
      if (asset.isCategory) {
        if (currentGroup) groups.push(currentGroup);
        const totalVal = assetTotalCountRefs.current[i]?.value;
        currentGroup = {
          group: asset.name,
          items: []
        };
        const resolvedTotal = totalVal !== undefined ? totalVal : asset.totalCount;
        if (resolvedTotal && resolvedTotal !== '') {
          currentGroup.total = parseInt(resolvedTotal) || resolvedTotal;
        }
      } else if (currentGroup) {
        const rawQty = assetSubCountRefs.current[i]?.value;
        const qty = rawQty !== undefined ? rawQty : asset.subCount;
        currentGroup.items.push({
          name: asset.name,
          qty: qty === '-' || qty === '' ? null : (parseInt(qty) || qty)
        });
      }
    });

    if (currentGroup) groups.push(currentGroup);
    return groups;
  };

  const saveToAPI = async () => {
    try {
      setIsSaving(true);
      const apiData = transformToApiFormat();
      const response = await apiCall("POST", "/boardpack/add-tables-data", apiData);
      if (response?.success) {
        // console.log("Data saved successfully");
        // toast.success("Data saved successfully");
      } else {
        throw new Error(response?.message || "Failed to update data");
      }
    } catch (err) {
      console.error("Error saving data:", err);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  if (!data) return <LoadingComponent/>;;

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="text-center mb-2 py-0.5 flex justify-between items-center">
        <div className='ml-2'>
          <img src={jeddhalogo} alt="Logo" style={{ width: '100%', height: '50px', objectFit: 'cover' }} />
        </div>
        <h1 className="text-2xl font-bold leading-tight flex-1 text-center">
          {profileName}
        </h1>
      </div>

      <div className="flex flex-row gap-6 !border-2 p-3">
        {/* Left Table - Violations Table & Fees */}
        <div className="flex-1">

          <div className="bg-white rounded px-3 py-2 w-fit mb-2">
            <div className="flex items-center text-sm">
              <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
              <strong className="font-semibold">
                <input
                  type="text"
                  defaultValue={section}
                  ref={sectionRef}
                  onChange={() => autoSave()}
                  className="border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 bg-transparent transition-colors outline-none"
                />
              </strong>
            </div>
          </div>

          <div className="bg-white">
            <div className="bg-gray-200 !border-b-1 !border-t-1 !border-r-1 !border-l-1 !border-black">
              <div className="p-1 text-center">
                <h2 className="text-[10px] font-bold text-gray-800">
                  Violations Table & Fees
                </h2>
              </div>
            </div>

            <div className="">
              <table className="w-full !border-collapse !border-t-1 text-[9px] leading-tight">
                <thead>
                  <tr className="bg-gray-100 !border-b-1 !border-black">
                    <th className="w-8 p-1.5 text-center font-bold text-gray-800 !border-r-1 !border-l-1 !border-black">#</th>
                    <th className="p-1.5 text-left font-bold text-gray-800 !border-r-1 !border-black">English</th>
                    <th className="w-12 p-1.5 text-center font-bold text-gray-800 !border-r-1 !border-black">Code</th>
                    <th className="w-20 p-1.5 text-center font-bold text-gray-800 !border-r-1 !border-black">Escalation</th>
                    <th className="w-12 p-2 text-center font-bold text-gray-800 !border-r-1 !border-black">Amount</th>
                    <th className="p-1.5 text-right font-bold text-gray-800 !border-r-1 !border-black">Arabic</th>
                    <th className="w-8 p-1.5 text-center font-bold text-gray-800 !border-r-1">#</th>
                  </tr>
                </thead>
                <tbody>
                  {violations.rows?.map((row, rowIndex) => {
                    // ensure row slot exists
                    if (!violationRefs.current[rowIndex]) {
                      violationRefs.current[rowIndex] = [];
                    }
                    return (
                      <tr key={rowIndex} className="!border-b !border-l !border-r !border-black">
                        {row.map((cell, colIndex) => (
                          <td
                            key={colIndex}
                            className={`p-1.5 !border-r !border-black ${
                              colIndex === 0 || colIndex === 6 ? 'text-center font-medium' :
                              colIndex === 2 || colIndex === 3 || colIndex === 4 ? 'text-center' :
                              colIndex === 5 ? 'text-right' : ''
                            }`}
                            dir={colIndex === 5 ? 'rtl' : 'ltr'}
                          >
                            <input
                              type="text"
                              defaultValue={cell || ''}
                              ref={(el) => (violationRefs.current[rowIndex][colIndex] = el)}
                              onChange={() => autoSave()}
                              disabled={cell === "NOTES :" || cell === "ملاحظة :" ? true : false}
                              className={`w-full border-0 text-center font-medium border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 bg-transparent transition-colors outline-none ${rowIndex === 21 && "text-red-500"} ${cell === "NOTES :" && "font-extrabold text-[14px]"} ${cell === "ملاحظة :" && "font-extrabold text-[14px]"}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Side Panel - Assets Deployed */}
        <AssetsPanel
          assetsTable={{ title: profileName }}
          flatAssets={assetsDeployed}
          onSubCount={(index, value) => {
            setData(prev => {
              if (!prev) return prev;
              const updatedAssets = [...prev.assetsDeployed];
              updatedAssets[index] = { ...updatedAssets[index], subCount: value };
              const newData = { ...prev, assetsDeployed: updatedAssets };
              dataRef.current = newData;
              return newData;
            });
            autoSave();
          }}
          onTotalCount={(index, value) => {
            setData(prev => {
              if (!prev) return prev;
              const updatedAssets = [...prev.assetsDeployed];
              updatedAssets[index] = { ...updatedAssets[index], totalCount: value };
              const newData = { ...prev, assetsDeployed: updatedAssets };
              dataRef.current = newData;
              return newData;
            });
            autoSave();
          }}
          subCountRefs={assetSubCountRefs}
          totalCountRefs={assetTotalCountRefs}
        />
      </div>
    </div>
  );
};

export default AirportEnforcementProfileTable;